import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, RotateCcw } from "lucide-react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import {
  CompletionByDevicePeriod,
  completionByDevicePeriods,
} from "../api/contracts";
import { ActivityTemplateFilterField } from "../components/ActivityTemplateFilterField";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import {
  CompletionByDeviceChart,
  ReportWindowBanner,
} from "../components/ReportVisuals";
import { useActivityTemplateTypeLookup } from "../hooks/useActivityTemplateTypeLookup";
import { usePortalData } from "../hooks/usePortalData";
import { isActivatedClientStatus } from "../utils/clientStatus";
import { formatPortalDate } from "../utils/dateTime";

const periodLabels: Record<CompletionByDevicePeriod, string> = {
  "this-week": "This Week",
  "last-30-days": "Last 30 Days",
  custom: "Custom",
};

function initialFilters() {
  const params = new URLSearchParams(window.location.search);
  const requestedPeriod = params.get(
    "period",
  ) as CompletionByDevicePeriod | null;
  return {
    patientId: params.get("patientId")?.trim() ?? "",
    activityTemplateId: params.get("activityTemplateId")?.trim() ?? "",
    period: completionByDevicePeriods.includes(
      requestedPeriod as CompletionByDevicePeriod,
    )
      ? (requestedPeriod as CompletionByDevicePeriod)
      : ("last-30-days" as const),
    startDate: params.get("startDate")?.trim() ?? "",
    endDate: params.get("endDate")?.trim() ?? "",
  };
}

export function CompletionByDevicePage({
  clinicianCode,
}: {
  clinicianCode: string;
}) {
  const initial = useMemo(initialFilters, []);
  const [patientId, setPatientId] = useState(initial.patientId);
  const [activityTemplateId, setActivityTemplateId] = useState(
    initial.activityTemplateId,
  );
  const [period, setPeriod] = useState<CompletionByDevicePeriod>(
    initial.period,
  );
  const [startDate, setStartDate] = useState(initial.startDate);
  const [endDate, setEndDate] = useState(initial.endDate);
  const activityTypeByTemplateId = useActivityTemplateTypeLookup(clinicianCode);
  const customDatesValid =
    period !== "custom" ||
    (Boolean(startDate) && Boolean(endDate) && startDate <= endDate);

  useEffect(() => {
    const url = new URL(window.location.href);
    patientId
      ? url.searchParams.set("patientId", patientId)
      : url.searchParams.delete("patientId");
    activityTemplateId
      ? url.searchParams.set("activityTemplateId", activityTemplateId)
      : url.searchParams.delete("activityTemplateId");
    period === "last-30-days"
      ? url.searchParams.delete("period")
      : url.searchParams.set("period", period);
    if (period === "custom") {
      startDate
        ? url.searchParams.set("startDate", startDate)
        : url.searchParams.delete("startDate");
      endDate
        ? url.searchParams.set("endDate", endDate)
        : url.searchParams.delete("endDate");
    } else {
      url.searchParams.delete("startDate");
      url.searchParams.delete("endDate");
    }
    window.history.replaceState({}, "", `${url.pathname}${url.search}`);
  }, [activityTemplateId, endDate, patientId, period, startDate]);

  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getCompletionByDevice(
        clinicianCode,
        patientId,
        activityTemplateId,
        period,
        startDate,
        endDate,
        signal,
      ),
    [activityTemplateId, clinicianCode, endDate, patientId, period, startDate],
  );
  const state = usePortalData(request, { enabled: customDatesValid });
  const responseData =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const data =
    responseData?.filters.period === period &&
    responseData.filters.patientId === (patientId || null) &&
    responseData.filters.activityTemplateId === (activityTemplateId || null) &&
    (period !== "custom" ||
      (responseData.filters.startDate === startDate &&
        responseData.filters.endDate === endDate))
      ? responseData
      : undefined;
  const available = Boolean(data?.found && data.authorized && data.therapist);
  const clients = useMemo(
    () =>
      (responseData?.clientOptions ?? []).filter((client) =>
        isActivatedClientStatus(client.status),
      ),
    [responseData?.clientOptions],
  );
  const activities = responseData?.activityOptions ?? [];

  useEffect(() => {
    if (
      responseData &&
      patientId &&
      !clients.some((client) => client.patientId === patientId)
    ) {
      setPatientId("");
    }
  }, [clients, patientId, responseData]);

  const resetFilters = () => {
    setPatientId("");
    setActivityTemplateId("");
    setPeriod("last-30-days");
    setStartDate("");
    setEndDate("");
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey="completion-by-device"
      clinicianCode={clinicianCode}
      therapistRole={responseData?.therapist?.role}
    >
      <PortalHeader
        title="Completion by Device"
        clinicianName={responseData?.therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[{ label: "Reports" }, { label: "Completion by Device" }]}
          />
        }
      />

      <div className="cp-page-content cp-page-content--wide cp-page-content--profile">
        <section className="cp-device-report">
          <div className="cp-device-report__intro">
            <div>
              <h2>Homework Completion by Device</h2>
              <p>
                Compare completed homework assignments by the device used to
                finish them.
              </p>
            </div>
            <button
              type="button"
              className="cp-button cp-button--secondary"
              disabled={
                state.status === "loading" || state.status === "refreshing"
              }
              onClick={state.refresh}
            >
              <RefreshCw aria-hidden="true" />
              {state.status === "refreshing" ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          <div className="cp-device-report__filters">
            <label>
              <span>Client</span>
              <select
                value={patientId}
                onChange={(event) => setPatientId(event.target.value)}
              >
                <option value="">All My Clients</option>
                {clients.map((client) => (
                  <option key={client.patientId} value={client.patientId}>
                    {client.nickname} · {client.accessCode}
                  </option>
                ))}
              </select>
            </label>
            <ActivityTemplateFilterField
              id="completion-by-device-activity-template"
              value={activityTemplateId}
              options={activities}
              activityTypeByTemplateId={activityTypeByTemplateId}
              onChange={setActivityTemplateId}
            />
            <label>
              <span>Completed Period</span>
              <select
                value={period}
                onChange={(event) =>
                  setPeriod(event.target.value as CompletionByDevicePeriod)
                }
              >
                {completionByDevicePeriods.map((option) => (
                  <option key={option} value={option}>
                    {periodLabels[option]}
                  </option>
                ))}
              </select>
            </label>
            {period === "custom" ? (
              <>
                <label>
                  <span>Start Date</span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                  />
                </label>
                <label>
                  <span>End Date</span>
                  <input
                    type="date"
                    min={startDate || undefined}
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                  />
                </label>
              </>
            ) : null}
            <button
              type="button"
              className="cp-button cp-button--secondary cp-device-report__reset"
              onClick={resetFilters}
            >
              <RotateCcw aria-hidden="true" />
              Reset Filters
            </button>
          </div>

          {period === "custom" && !customDatesValid ? (
            <EmptyState
              title="Choose a valid date range"
              description="Select a start date and an end date on or after the start date."
            />
          ) : null}
          {customDatesValid &&
          (state.status === "loading" ||
            (state.status === "refreshing" && !data)) ? (
            <LoadingState label="Loading completion by device" />
          ) : null}
          {customDatesValid && state.status === "error" ? (
            <ErrorState error={state.error} onRetry={state.refresh} />
          ) : null}
          {data && !available ? (
            <EmptyState
              title="Report unavailable"
              description="This client is unavailable or is not assigned to your account."
            />
          ) : null}
          {available && data ? (
            <div className="cp-device-report__body">
              <ReportWindowBanner className="cp-device-report__window">
                Completed-date window: {formatPortalDate(data.filters.startDate)}
                {" through "}
                {formatPortalDate(data.filters.endDate)} · As of{" "}
                {formatPortalDate(data.asOfDate)} ({data.timezone})
              </ReportWindowBanner>

              <section className="cp-device-report-card">
                <header>
                  <h3>Completed Assignments by Device</h3>
                  <p>
                    Count and share of completed homework recorded for each
                    device type.
                  </p>
                </header>
                <CompletionByDeviceChart
                  rows={data.deviceBreakdown.map((item) => ({
                    key: item.deviceKey,
                    label: item.deviceLabel,
                    count: item.completedCount,
                    percentage: item.percentage,
                  }))}
                />
              </section>
            </div>
          ) : null}
        </section>
      </div>
    </ClinicianPortalLayout>
  );
}
